import {
    createEmptyWorkingTime,
    createEmptyFinancialExpectations,
    createEmptyAvailability,
    createEmptyAiConversationConfig,
    createEmptyScoringConfig,
    createEmptySectionAdditionalInfo,
} from "@/models/jobOffer";
import {
    scoringConfigToApi,
    scoringConfigFromApi,
    createEmptyScoringConfig as createEmptyCriterionScoring,
} from "@/scoringUi";

/**
 * Well-known `Entity.Name`s for recruitment-form sections that carry their own
 * per-criterion scoring config but aren't backed by a Scenario entity (spec 2
 * §4). These become Recruitment.LocalCriteria entries at save time -- the
 * evaluation engine matches captured conversation values to a criterion by
 * this name, so it must line up with whatever the scenario's conversation
 * actually calls this collected value (a scenario-design concern, not
 * something the FE can guarantee alone -- flagged, not silently assumed).
 *
 * Single-object sections use one fixed, conventional name each (below).
 *
 * Array sections (employmentForms, motivationQuestions, requiredDocuments,
 * skills, experienceRequirements, riskFactors, customQuestions) are wired the same way but per-item: each
 * item's own already-required text field (aiQuestion / questionText / name /
 * area) IS its matching name -- see `buildArrayLocalCriterionRequests` below.
 * This needed no backend change (no stable id exists on these records, so the
 * requirement's own text stands in for one, same accepted-risk
 * name-based-matching convention as everything else here). Trade-off: editing
 * that text after configuring scoring orphans the link -- acceptable, same
 * risk class as the rest of this mechanism. Formerly tracked as "Faza 11" in
 * the plan doc; resolved this way rather than left open.
 *
 * requiredDocuments/skills/experienceRequirements additionally keep a
 * `legacyScoring` field (old weight-based shape) purely because the backend's
 * per-item `scoring` property is still a `NotNull()`-validated `ScoringCriteria`
 * value object -- see `reverseScoring`/`mapScoring` below. It is never shown
 * in the UI and never read by any evaluation logic; the new `scoring` field
 * (this mechanism) is what actually drives the candidate's Score.
 */
const LOCAL_CRITERION_NAMES = {
    availability: "dostepnosc",
    financialExpectations: "oczekiwaniaFinansowe",
};

/** Synthesizes the EntityRequest shape needed to carry a section's scoring config as a Recruitment.LocalCriteria item. The non-scoring Entity fields (conversation-flow only) are inert here since local criteria aren't part of any Intent. */
function buildLocalCriterionRequest(name, scoring) {
    return {
        name,
        displayName: name,
        completeResponse: "",
        response: "",
        nextIntent: "",
        prompts: [],
        skipPhrases: [],
        transitions: [],
        dynamicValues: [],
        isRequired: false,
        isConditional: false,
        scoring: scoringConfigToApi(scoring),
    };
}

/** Finds a Recruitment.LocalCriteria entry by name and returns its scoring config in this app's shape, or null if not present (new/never-saved recruitment). */
function findLocalCriterionScoring(localCriteria, name) {
    const match = (localCriteria ?? []).find((lc) => lc.name === name);
    return match?.scoring ? scoringConfigFromApi(match.scoring) : null;
}

/**
 * True when the user has actually configured this criterion's scoring panel -- the toggle,
 * a max score, a description, or any rule -- as opposed to the untouched default. Gating
 * persistence on `scoringEnabled` alone would silently drop that configuration on save: the
 * panel deliberately lets every field be filled in before the toggle is switched on ("so the
 * structure can be fully defined ahead of turning scoring on", see ScoringConfigPanel's UX
 * contract docstring), so a user who fills in maxScore/rules but leaves the toggle off must
 * still have that work saved.
 */
function isScoringConfigured(scoring) {
    if (!scoring) return false;
    return Boolean(
        scoring.scoringEnabled ||
        scoring.maxScore != null ||
        (scoring.description ?? "").trim() !== "" ||
        (scoring.valueRules?.length ?? 0) > 0 ||
        (scoring.rangeRules?.length ?? 0) > 0 ||
        (scoring.setRules?.length ?? 0) > 0 ||
        (scoring.aiCriterion ?? "").trim() !== "" ||
        (scoring.aiLevels?.length ?? 0) > 0
    );
}

/** Builds one LocalCriteria request per array item whose scoring has been configured (see isScoringConfigured) and whose match-name field is non-empty (see LOCAL_CRITERION_NAMES doc above). Items without a name yet (still being filled in) or with a still-untouched scoring panel are silently skipped, not sent as broken criteria. */
function buildArrayLocalCriterionRequests(items, nameSelector) {
    return (items ?? [])
        .filter((item) => isScoringConfigured(item.scoring) && nameSelector(item)?.trim())
        .map((item) => buildLocalCriterionRequest(nameSelector(item).trim(), item.scoring));
}

/** Hydrates one array item's scoring config from Recruitment.LocalCriteria by its own match-name field, falling back to an empty (disabled) config for items that never had scoring configured. */
function findArrayItemScoring(localCriteria, name) {
    return findLocalCriterionScoring(localCriteria, name?.trim()) ?? createEmptyCriterionScoring();
}

const ENUM_MAP = {
    WORK_MODE: { stationary: "Stationary", hybrid: "Hybrid", remote: "Remote" },
    SENIORITY_LEVEL: { junior: "Junior", mid: "Mid", senior: "Senior", manager: "Manager" },
    OFFER_STATUS: { draft: "Draft", active: "Active", inactive: "Inactive" },
    EMPLOYMENT_FORM_TYPE: {
        employmentContract: "UoP", mandateContract: "Contract", contractForSpecificWork: "TaskContract", b2b: "B2B", contract: "Contract", temporaryWork: "TempWork", other: "Other",
    },
    SALARY_MODEL_TYPE: {
        hourlyRate: "Hourly", fixedMonthly: "Monthly", pieceRate: "Piecework",
        pieceRatePlusBase: "PieceworkPlusBase", basePlusBonus: "BasePlusBonuses",
        basePlusCommission: "BasePlusCommission", commission: "Commission",
        projectBased: "Project", other: "Other",
    },
    PIECE_RATE_UNIT: {
        piece: "Piece", course: "Course", order: "Order", kilogram: "Kilogram",
        meter: "Meter", service: "Service", project: "Project", other: "Other",
    },
    BILLING_PERIOD: { weekly: "Weekly", biweekly: "BiWeekly", monthly: "Monthly", other: "Other" },
    BONUS_TYPE: {
        monthly: "Monthly", quarterly: "Quarterly", annual: "Annual", discretionary: "Discretionary",
        commission: "Commission", attendance: "Attendance", quality: "Quality", safety: "Safety", other: "Other",
    },
    ALLOWANCE_TYPE: {
        night: "Night", shift: "Shift", overtime: "Overtime", businessTrip: "Travel",
        specialConditions: "SpecialConditions", carAllowance: "CarAllowance",
        remoteWorkAllowance: "RemoteWorkAllowance", other: "Other",
    },
    WORK_DIMENSION: {
        fullTime: "FullTime", threeQuarterTime: "ThreeQuarter", halfTime: "HalfTime",
        partTime: "PartTime", seasonal: "Seasonal", project: "Project",
    },
    WORK_SYSTEM: { oneShift: "SingleShift", twoShift: "TwoShift", threeShift: "ThreeShift", fourBrigade: "FourBrigade", taskBased: "Task" },
    OVERTIME_FREQUENCY: { none: "None", occasional: "Sporadic", fewTimesAMonth: "FewTimesPerMonth", regular: "Regular" },
    OVERTIME_SETTLEMENT: { paidExtra: "ExtraPayment", timeOff: "TimeOff", both: "Both", other: "Other" },
    DOCUMENT_TYPE: {
        cv: "CV", drivingLicense: "DrivingLicense", professionalLicense: "ProfessionalLicense",
        certificate: "Certificate", sanitaryBook: "HealthBook", confirmation: "Statement",
        portfolio: "Portfolio", other: "Other",
    },
    DOCUMENT_STATUS: { required: "Required", optional: "Optional", niceToHave: "NiceToHave" },
    CRITICALITY: { critical: "Critical", important: "Important", additional: "Additional" },
    SKILL_CATEGORY: {
        technical: "Technical", soft: "Soft", language: "Language", sales: "Sales",
        industry: "Industry", managerial: "Management", other: "Other",
    },
    SKILL_STATUS: { required: "Required", optional: "Optional", niceToHave: "NiceToHave" },
    SKILL_SCALE_TYPE: { scale1to5: "OneToFive", scale1to10: "OneToTen", levelWords: "LevelBased", yesNo: "YesNo" },
    CONFIRMATION_TYPE: {
        certificate: "Certificate", course: "Course", portfolio: "Portfolio",
        references: "References", workExperience: "WorkExperience", none: "None",
    },
    ANSWER_TYPE: {
        text: "Text", yesNo: "YesNo", number: "Number", date: "Date",
        scale: "Scale", singleChoice: "SingleChoice", multiChoice: "MultipleChoice",
    },
    CONVERSATION_STYLE: { formal: "Formal", professional: "Professional", friendly: "Friendly", casual: "Casual" },
};

const REVERSE_ENUM_MAP = Object.fromEntries(
    Object.entries(ENUM_MAP).map(([mapName, mapping]) => [
        mapName,
        Object.fromEntries(Object.entries(mapping).map(([k, v]) => [v, k])),
    ])
);

function e(map, value) {
    if (!value) return null;
    return ENUM_MAP[map][value] ?? value;
}

function re(map, value) {
    if (!value) return null;
    return REVERSE_ENUM_MAP[map]?.[value] ?? value;
}

const WorkConditionFlag = {
    PhysicalWork: 1,
    WorkAtHeight: 2,
    MachineWork: 4,
    NightWork: 8,
    OutdoorWork: 16,
    VehicleOperation: 32,
    ChemicalContact: 64,
    NoisyEnvironment: 128,
    DustyEnvironment: 256,
    CustomerContact: 512,
    Other: 1024,
};

function mapWorkConditions(wc) {
    if (!wc) return 0;
    let flags = 0;
    if (wc.physicalWork) flags |= WorkConditionFlag.PhysicalWork;
    if (wc.workingAtHeight) flags |= WorkConditionFlag.WorkAtHeight;
    if (wc.workingWithMachinery) flags |= WorkConditionFlag.MachineWork;
    if (wc.nightWork) flags |= WorkConditionFlag.NightWork;
    if (wc.outdoorWork) flags |= WorkConditionFlag.OutdoorWork;
    if (wc.drivingVehicles) flags |= WorkConditionFlag.VehicleOperation;
    if (wc.chemicalSubstancesContact) flags |= WorkConditionFlag.ChemicalContact;
    if (wc.noiseExposure) flags |= WorkConditionFlag.NoisyEnvironment;
    if (wc.dustExposure) flags |= WorkConditionFlag.DustyEnvironment;
    if (wc.customerContact) flags |= WorkConditionFlag.CustomerContact;
    if (wc.other) flags |= WorkConditionFlag.Other;
    return flags;
}

function mapEmploymentForm(ef) {
    return {
        formType: (ef.type || []).map(v => e("EMPLOYMENT_FORM_TYPE", v)),
        preferredForm: e("EMPLOYMENT_FORM_TYPE", ef.preferredType),
        isEliminating: ef.isEliminatingIfNotAccepted,
        weight: ef.weight,
        questionText: ef.aiQuestion || null,
    };
}

function mapSalaryModel(sm) {
    return {
        modelType: e("SALARY_MODEL_TYPE", sm.type),
        currency: sm.currency || null,
        hasBonus: sm.hasBonus,
        canCommunicateToCandidate: sm.canCommunicateToCandidate,
        negotiable: sm.isNegotiable,
        hourlyRateFrom: sm.hourly?.rateFrom ?? null,
        hourlyRateTo: sm.hourly?.rateTo ?? null,
        overtimeHasDifferentRate: sm.hourly?.hasDifferentOvertimeRate ?? null,
        overtimeRateDescription: sm.hourly?.overtimeDescription ?? null,
        monthlyGrossFrom: sm.fixedSalary?.grossMonthlyFrom ?? null,
        monthlyGrossTo: sm.fixedSalary?.grossMonthlyTo ?? null,
        isFixedSalary: sm.fixedSalary?.isFixedDescription ?? null,
        pieceworkUnit: e("PIECE_RATE_UNIT", sm.pieceRate?.unit),
        pricePerUnit: sm.pieceRate?.pricePerUnit ?? null,
        averageUnitsDescription: sm.pieceRate?.avgUnitsDescription ?? null,
        calculationDescription: sm.pieceRate?.calculationDescription ?? null,
        averageMonthlyEarnings: sm.pieceRate?.avgMonthlySalary ?? null,
        minimumEarnings: sm.pieceRate?.minSalary ?? null,
        maximumEarnings: sm.pieceRate?.maxSalary ?? null,
        b2BNetFrom: sm.b2b?.netInvoiceAmountFrom ?? null,
        b2BNetTo: sm.b2b?.netInvoiceAmountTo ?? null,
        b2BBillingPeriod: e("BILLING_PERIOD", sm.b2b?.settlementPeriod),
        b2BPaymentTerms: sm.b2b?.paymentTerm ?? null,
    };
}

function mapBonus(b) {
    return {
        name: b.name,
        bonusType: e("BONUS_TYPE", b.type),
        isGuaranteed: b.isGuaranteed,
        calculationMethod: b.calculationDescription,
        amountFrom: b.amountFrom,
        amountTo: b.amountTo,
        canCommunicateToCandidate: b.canCommunicateToCandidate,
    };
}

function mapAllowance(a) {
    return {
        name: a.name,
        allowanceType: e("ALLOWANCE_TYPE", a.type),
        calculationMethod: a.calculationDescription,
        amountFrom: a.amountFrom,
        amountTo: a.amountTo,
        canCommunicateToCandidate: a.canCommunicateToCandidate,
    };
}

function mapWorkingTime(wt) {
    if (!wt) return null;
    return {
        dimension: e("WORK_DIMENSION", wt.workloadType),
        minWeeklyHours: wt.minHoursPerWeek,
        maxWeeklyHours: wt.maxHoursPerWeek,
        workSystem: e("WORK_SYSTEM", wt.workSystem),
        startTime: wt.startTime,
        endTime: wt.endTime,
        fixedSchedule: wt.isFixedSchedule,
        flexibleSchedule: wt.isFlexibleSchedule,
        weekendWork: wt.worksWeekends,
        holidayWork: wt.worksHolidays,
        delegations: wt.hasBusinessTrips,
        nightWork: wt.worksNightShifts,
        onCall: wt.hasOnCallDuty,
        overtime: wt.overtime ? {
            hasOvertime: wt.overtime.exists,
            frequency: e("OVERTIME_FREQUENCY", wt.overtime.frequency),
            isMandatory: wt.overtime.isMandatory,
            settlement: e("OVERTIME_SETTLEMENT", wt.overtime.settlementMethod),
            isEliminating: wt.overtime.refusalIsEliminating,
        } : null,
    };
}

function mapRiskFactor(rf) {
    return {
        name: rf.name,
        description: rf.description,
        askCandidate: rf.aiShouldAsk,
        isEliminating: rf.refusalIsEliminating,
        weight: rf.weight,
    };
}

function mapProtectiveEquipment(pe) {
    return {
        name: pe.name,
        providedByEmployer: pe.providedByEmployer,
        requiredFromEmployee: pe.requiredFromEmployee,
        mandatory: pe.mandatoryForPosition,
        isEliminating: pe.refusalIsEliminating,
        weight: pe.weight,
        questionText: pe.aiQuestion,
    };
}

function mapScoring(sc) {
    if (!sc) return undefined;
    return {
        isRequired: sc.isRequired,
        isCritical: sc.isCritical,
        isEliminating: sc.isEliminating,
        weight: sc.weight,
        minAcceptedValue: sc.minAcceptedValue,
        preferredValue: sc.preferredValue,
        askCandidate: sc.askCandidate,
        questionText: sc.questionText,
        followUpQuestionText: sc.followUpQuestionText,
        internalComment: sc.internalComment,
    };
}

function mapRequiredDocument(rd) {
    return {
        name: rd.name,
        documentType: e("DOCUMENT_TYPE", rd.documentType),
        status: e("DOCUMENT_STATUS", rd.status),
        criticality: e("CRITICALITY", rd.criticality),
        requireUpload: rd.requiresUpload,
        mustBeCurrent: rd.mustBeCurrent,
        additionalInfo: rd.additionalInfo || null,
        scoring: mapScoring(rd.legacyScoring),
    };
}

function mapSkill(sk) {
    return {
        name: sk.name,
        category: e("SKILL_CATEGORY", sk.category),
        status: e("SKILL_STATUS", sk.status),
        criticality: e("CRITICALITY", sk.criticality),
        minLevel: sk.minLevel ?? 0,
        scaleType: e("SKILL_SCALE_TYPE", sk.ratingScale),
        requiresConfirmation: sk.requiresConfirmation,
        confirmationType: e("CONFIRMATION_TYPE", sk.confirmationType),
        additionalInfo: sk.additionalInfo || null,
        scoring: mapScoring(sk.legacyScoring),
    };
}

function mapSectionAdditionalInfo(sai) {
    if (!sai) return null;
    return {
        basicInfo: sai.basicInfo || null,
        positionDescription: sai.positionDescription || null,
        employmentForms: sai.employmentForms || null,
        salaryModels: sai.salaryModels || null,
        bonusesAndAllowances: sai.bonusesAndAllowances || null,
        workingTime: sai.workingTime || null,
        safetyAndConditions: sai.safetyAndConditions || null,
        protectiveEquipment: sai.protectiveEquipment || null,
        motivation: sai.motivation || null,
        financialExpectations: sai.financialExpectations || null,
        availability: sai.availability || null,
        experience: sai.experience || null,
    };
}

function mapExperienceRequirement(er) {
    return {
        area: er.area,
        minYears: er.minYears,
        status: e("SKILL_STATUS", er.status),
        scoring: mapScoring(er.legacyScoring),
    };
}

function mapCustomQuestion(cq) {
    return {
        questionText: cq.questionText,
        answerType: e("ANSWER_TYPE", cq.answerType),
        isRequired: cq.isRequired,
        isEliminating: cq.isEliminating,
        expectedAnswer: cq.expectedAnswer,
        eliminatingAnswer: cq.eliminatingAnswer,
        weight: cq.weight,
        canFollowUp: cq.aiCanFollowUp,
        followUpQuestionText: cq.followUpQuestionText,
    };
}

function mapMotivationQuestion(mq) {
    return {
        questionText: mq.questionText,
        expectedAnswer: mq.expectedAnswer,
        positiveSignals: mq.positiveSignals,
        riskSignals: mq.riskSignals,
        weight: mq.weight,
        canEliminate: mq.canAnswerEliminate,
    };
}

function mapFinancialExpectations(fe) {
    if (!fe) return null;
    return {
        askCandidate: fe.aiAsksAboutExpectations,
        acceptableFrom: fe.acceptableRangeFrom,
        acceptableTo: fe.acceptableRangeTo,
        preferredFrom: fe.preferredRangeFrom,
        preferredTo: fe.preferredRangeTo,
        eliminationThreshold: fe.eliminatingAbove,
        currency: fe.currency,
        isEliminating: fe.exceedingBudgetIsEliminating,
        weight: fe.weight,
    };
}

function mapAvailability(av) {
    if (!av) return null;
    return {
        earliestAcceptableStartDate: av.earliestAcceptedStartDate,
        maxNoticePeriodDays: av.maxNoticePeriodDays,
        askCandidate: av.aiAsksAboutAvailability,
        isEliminating: av.lackOfQuickAvailabilityIsEliminating,
        weight: av.weight,
        readyForShifts: av.requiresShiftWorkReadiness,
        readyForWeekends: av.requiresWeekendReadiness,
        readyForDelegations: av.requiresBusinessTripReadiness,
        readyForOvertime: av.requiresOvertimeReadiness,
    };
}

function mapDisqualificationRule(dr) {
    return {
        name: dr.name,
        condition: dr.conditionDescription,
        relatedField: dr.relatedFormField,
        eliminatingValue: dr.eliminatingValue,
        autoEliminate: dr.isAutomaticElimination,
        requiresManualDecision: dr.requiresManualDecision,
        recruiterComment: dr.recruiterComment,
    };
}

function mapConversationConfig(cc) {
    if (!cc) return null;
    return {
        style: e("CONVERSATION_STYLE", cc.conversationStyle),
        language: cc.language || null,
        canAnswerAboutSalary: cc.canAnswerAboutSalary,
        canAnswerAboutBenefits: cc.canAnswerAboutBenefits,
        canAnswerAboutCompany: cc.canAnswerAboutCompany,
        confidentialInfo: cc.confidentialInfo,
        fallbackAnswer: cc.fallbackAnswer,
        maxFollowUpQuestions: cc.maxFollowUpQuestions,
    };
}

function mapScoringConfig(sc) {
    if (!sc) return null;
    const cmp = sc.categoryMaxPoints;
    return {
        maxPoints: sc.maxTotalPoints,
        excellentMatchThreshold: sc.veryGoodMatchThreshold,
        goodMatchThreshold: sc.goodMatchThreshold,
        partialMatchThreshold: sc.partialMatchThreshold,
        eliminationBlocksRanking: sc.eliminationBlocksRanking,
        categoryWeights: cmp ? {
            documents: cmp.documents,
            criticalSkills: cmp.criticalSkills,
            optionalSkills: cmp.optionalSkills,
            experience: cmp.experience,
            availability: cmp.availability,
            financialExpectations: cmp.financialExpectations,
            motivation: cmp.motivation,
            safetyAndWorkConditions: cmp.safety,
            overtimeAndShifts: cmp.overtimeAndShifts,
            customQuestions: cmp.customQuestions,
        } : null,
    };
}

function reverseScoring(sc) {
    if (!sc) return { isRequired: false, isCritical: false, isEliminating: false, weight: 0, minAcceptedValue: null, preferredValue: null, askCandidate: false, questionText: "", followUpQuestionText: "", internalComment: "" };
    return {
        isRequired: sc.isRequired ?? false,
        isCritical: sc.isCritical ?? false,
        isEliminating: sc.isEliminating ?? false,
        weight: sc.weight ?? 0,
        minAcceptedValue: sc.minAcceptedValue ?? null,
        preferredValue: sc.preferredValue ?? null,
        askCandidate: sc.askCandidate ?? false,
        questionText: sc.questionText ?? "",
        followUpQuestionText: sc.followUpQuestionText ?? "",
        internalComment: sc.internalComment ?? "",
    };
}

function reverseWorkConditions(flags) {
    const f = flags ?? 0;
    return {
        physicalWork: !!(f & WorkConditionFlag.PhysicalWork),
        workingAtHeight: !!(f & WorkConditionFlag.WorkAtHeight),
        workingWithMachinery: !!(f & WorkConditionFlag.MachineWork),
        nightWork: !!(f & WorkConditionFlag.NightWork),
        outdoorWork: !!(f & WorkConditionFlag.OutdoorWork),
        drivingVehicles: !!(f & WorkConditionFlag.VehicleOperation),
        chemicalSubstancesContact: !!(f & WorkConditionFlag.ChemicalContact),
        noiseExposure: !!(f & WorkConditionFlag.NoisyEnvironment),
        dustExposure: !!(f & WorkConditionFlag.DustyEnvironment),
        customerContact: !!(f & WorkConditionFlag.CustomerContact),
        other: "",
    };
}

export function fromApiResponse(r) {
    // Names already "owned" by an array section's own per-item scoring (matched via
    // buildArrayLocalCriterionRequests on save) -- these must NOT be preserved in the
    // localCriteria pass-through below, or every save re-adds a fresh entry for the
    // same item alongside the stale one carried over from load, duplicating it in
    // Recruitment.LocalCriteria (confirmed: doubles that criterion's contribution to
    // totalMaxScore on every re-save). Mirrors the same match-key fields used by
    // buildArrayLocalCriterionRequests for each of these sections.
    const ownedLocalCriterionNames = new Set(
        [
            ...(r.employmentForms ?? []).map((ef) => ef.questionText),
            ...(r.motivationQuestions ?? []).map((mq) => mq.questionText),
            ...(r.requiredDocuments ?? []).map((rd) => rd.name),
            ...(r.skills ?? []).map((sk) => sk.name),
            ...(r.experienceRequirements ?? []).map((er) => er.area),
            ...(r.riskFactors ?? []).map((rf) => rf.name),
            ...(r.customQuestions ?? []).map((cq) => cq.questionText),
        ]
            .filter(Boolean)
            .map((name) => name.trim())
    );
    return {
        id: r.id ?? null,
        scenarioId: r.scenario?.id ?? null,
        basicInfo: {
            positionName: r.jobTitle ?? "",
            department: r.department ?? "",
            companyName: r.companyName ?? "",
            companyDescription: r.companyDescription ?? "",
            location: r.location ?? "",
            workMode: re("WORK_MODE", r.workMode) ?? "stationary",
            vacancies: r.vacancyCount ?? null,
            plannedStartDate: r.plannedStartDate ? r.plannedStartDate.slice(0, 10) : "",
            plannedStartDateIsAsap: r.plannedStartDateIsAsap ?? false,
            seniorityLevel: re("SENIORITY_LEVEL", r.seniorityLevel),
            offerStatus: re("OFFER_STATUS", r.status) ?? "draft",
            sendNotifications: r.sendNotifications ?? false,
            notificationEmails: Array.isArray(r.notificationEmails)
                ? r.notificationEmails.filter(Boolean)
                : r.notificationEmails
                    ? r.notificationEmails.split(",").map((e) => e.trim()).filter(Boolean)
                    : [],
        },
        positionDescription: {
            shortDescription: r.shortDescription ?? "",
            mainResponsibilities: r.mainDuties ?? [],
            responsibilitiesScope: r.responsibilities ?? [],
            positionGoals: r.goals ?? "",
            typicalWorkday: r.typicalDay ?? "",
            teamStructure: r.teamStructure ?? "",
            supervisor: r.supervisor ?? "",
            employeeOnboarding: r.employeeOnboarding ?? "",
            toolsAndSystems: r.tools ?? [],
            benefits: r.benefits ?? [],
            recruitmentStages: r.recruitmentStages ?? [],
        },
        employmentForms: (r.employmentForms ?? []).map(ef => ({
            type: (ef.formType ?? []).map(v => re("EMPLOYMENT_FORM_TYPE", v)),
            preferredType: re("EMPLOYMENT_FORM_TYPE", ef.preferredForm) ?? "",
            isEliminatingIfNotAccepted: ef.isEliminating ?? false,
            weight: ef.weight ?? 0,
            aiQuestion: ef.questionText ?? "",
            scoring: findArrayItemScoring(r.localCriteria, ef.questionText),
        })),
        salaryModels: (r.salaryModels ?? []).map(sm => ({
            type: re("SALARY_MODEL_TYPE", sm.modelType),
            currency: sm.currency ?? "PLN",
            hasBonus: sm.hasBonus ?? false,
            canCommunicateToCandidate: sm.canCommunicateToCandidate ?? false,
            isNegotiable: sm.negotiable ?? false,
            hourly: (sm.hourlyRateFrom != null || sm.hourlyRateTo != null) ? {
                rateFrom: sm.hourlyRateFrom,
                rateTo: sm.hourlyRateTo,
                hasDifferentOvertimeRate: sm.overtimeHasDifferentRate ?? false,
                overtimeDescription: sm.overtimeRateDescription ?? "",
            } : null,
            fixedSalary: (sm.monthlyGrossFrom != null || sm.monthlyGrossTo != null) ? {
                grossMonthlyFrom: sm.monthlyGrossFrom,
                grossMonthlyTo: sm.monthlyGrossTo,
                isFixedDescription: sm.isFixedSalary ?? "",
            } : null,
            pieceRate: sm.pieceworkUnit ? {
                unit: re("PIECE_RATE_UNIT", sm.pieceworkUnit),
                pricePerUnit: sm.pricePerUnit,
                avgUnitsDescription: sm.averageUnitsDescription,
                calculationDescription: sm.calculationDescription,
                avgMonthlySalary: sm.averageMonthlyEarnings,
                minSalary: sm.minimumEarnings,
                maxSalary: sm.maximumEarnings,
            } : null,
            b2b: (sm.b2BNetFrom != null || sm.b2BNetTo != null) ? {
                netInvoiceAmountFrom: sm.b2BNetFrom,
                netInvoiceAmountTo: sm.b2BNetTo,
                settlementPeriod: re("BILLING_PERIOD", sm.b2BBillingPeriod),
                paymentTerm: sm.b2BPaymentTerms ?? "",
            } : null,
        })),
        bonuses: (r.bonuses ?? []).map(b => ({
            name: b.name ?? "",
            type: re("BONUS_TYPE", b.bonusType),
            isGuaranteed: b.isGuaranteed ?? false,
            calculationDescription: b.calculationMethod ?? "",
            amountFrom: b.amountFrom ?? null,
            amountTo: b.amountTo ?? null,
            canCommunicateToCandidate: b.canCommunicateToCandidate ?? false,
        })),
        allowances: (r.allowances ?? []).map(a => ({
            name: a.name ?? "",
            type: re("ALLOWANCE_TYPE", a.allowanceType),
            calculationDescription: a.calculationMethod ?? "",
            amountFrom: a.amountFrom ?? null,
            amountTo: a.amountTo ?? null,
            canCommunicateToCandidate: a.canCommunicateToCandidate ?? false,
        })),
        workingTime: r.workingTime ? {
            workloadType: re("WORK_DIMENSION", r.workingTime.dimension) ?? "fullTime",
            minHoursPerWeek: r.workingTime.minWeeklyHours ?? null,
            maxHoursPerWeek: r.workingTime.maxWeeklyHours ?? null,
            workSystem: re("WORK_SYSTEM", r.workingTime.workSystem) ?? "oneShift",
            startTime: r.workingTime.startTime ?? "",
            endTime: r.workingTime.endTime ?? "",
            isFixedSchedule: r.workingTime.fixedSchedule ?? false,
            isFlexibleSchedule: r.workingTime.flexibleSchedule ?? false,
            worksWeekends: r.workingTime.weekendWork ?? false,
            worksHolidays: r.workingTime.holidayWork ?? false,
            hasBusinessTrips: r.workingTime.delegations ?? false,
            worksNightShifts: r.workingTime.nightWork ?? false,
            hasOnCallDuty: r.workingTime.onCall ?? false,
            overtime: r.workingTime.overtime ? {
                exists: r.workingTime.overtime.hasOvertime ?? false,
                frequency: re("OVERTIME_FREQUENCY", r.workingTime.overtime.frequency),
                isMandatory: r.workingTime.overtime.isMandatory ?? false,
                settlementMethod: re("OVERTIME_SETTLEMENT", r.workingTime.overtime.settlement),
                refusalIsEliminating: r.workingTime.overtime.isEliminating ?? false,
            } : { exists: false, frequency: "none", isMandatory: false, settlementMethod: "timeOff", refusalIsEliminating: false },
        } : createEmptyWorkingTime(),
        workConditions: reverseWorkConditions(r.workConditions),
        riskFactors: (r.riskFactors ?? []).map(rf => ({
            name: rf.name ?? "",
            description: rf.description ?? "",
            aiShouldAsk: rf.askCandidate ?? false,
            refusalIsEliminating: rf.isEliminating ?? false,
            weight: rf.weight ?? 0,
            scoring: findArrayItemScoring(r.localCriteria, rf.name),
        })),
        protectiveEquipment: (r.protectiveEquipment ?? []).map(pe => ({
            name: pe.name ?? "",
            providedByEmployer: pe.providedByEmployer ?? false,
            requiredFromEmployee: pe.requiredFromEmployee ?? false,
            mandatoryForPosition: pe.mandatory ?? false,
            refusalIsEliminating: pe.isEliminating ?? false,
            weight: pe.weight ?? 0,
            aiQuestion: pe.questionText ?? "",
        })),
        requiredDocuments: (r.requiredDocuments ?? []).map(rd => ({
            name: rd.name ?? "",
            documentType: re("DOCUMENT_TYPE", rd.documentType),
            status: re("DOCUMENT_STATUS", rd.status),
            criticality: re("CRITICALITY", rd.criticality),
            requiresUpload: rd.requireUpload ?? false,
            mustBeCurrent: rd.mustBeCurrent ?? false,
            additionalInfo: rd.additionalInfo ?? "",
            legacyScoring: reverseScoring(rd.scoring),
            scoring: findArrayItemScoring(r.localCriteria, rd.name),
        })),
        skills: (r.skills ?? []).map(sk => ({
            name: sk.name ?? "",
            category: re("SKILL_CATEGORY", sk.category),
            status: re("SKILL_STATUS", sk.status),
            criticality: re("CRITICALITY", sk.criticality),
            ratingScale: re("SKILL_SCALE_TYPE", sk.scaleType),
            minLevel: sk.minLevel ?? null,
            aiAsksLevel: sk.scoring?.askCandidate ?? false,
            requiresConfirmation: sk.requiresConfirmation ?? false,
            confirmationType: re("CONFIRMATION_TYPE", sk.confirmationType) ?? "none",
            additionalInfo: sk.additionalInfo ?? "",
            legacyScoring: reverseScoring(sk.scoring),
            scoring: findArrayItemScoring(r.localCriteria, sk.name),
        })),
        experienceRequirements: (r.experienceRequirements ?? []).map(er => ({
            area: er.area ?? "",
            minYears: er.minYears ?? 0,
            status: re("SKILL_STATUS", er.status),
            legacyScoring: reverseScoring(er.scoring),
            scoring: findArrayItemScoring(r.localCriteria, er.area),
        })),
        customQuestions: (r.customQuestions ?? []).map(cq => ({
            questionText: cq.questionText ?? "",
            answerType: re("ANSWER_TYPE", cq.answerType),
            isRequired: cq.isRequired ?? false,
            isEliminating: cq.isEliminating ?? false,
            expectedAnswer: cq.expectedAnswer ?? "",
            eliminatingAnswer: cq.eliminatingAnswer ?? "",
            weight: cq.weight ?? 0,
            aiCanFollowUp: cq.canFollowUp ?? false,
            followUpQuestionText: cq.followUpQuestionText ?? "",
            scoring: findArrayItemScoring(r.localCriteria, cq.questionText),
        })),
        motivationQuestions: (r.motivationQuestions ?? []).map(mq => ({
            questionText: mq.questionText ?? "",
            expectedAnswer: mq.expectedAnswer ?? "",
            positiveSignals: mq.positiveSignals ?? "",
            riskSignals: mq.riskSignals ?? "",
            weight: mq.weight ?? 0,
            canAnswerEliminate: mq.canEliminate ?? false,
            scoring: findArrayItemScoring(r.localCriteria, mq.questionText),
        })),
        financialExpectations: r.financialExpectations ? {
            aiAsksAboutExpectations: r.financialExpectations.askCandidate ?? true,
            acceptableRangeFrom: r.financialExpectations.acceptableFrom ?? null,
            acceptableRangeTo: r.financialExpectations.acceptableTo ?? null,
            preferredRangeFrom: r.financialExpectations.preferredFrom ?? null,
            preferredRangeTo: r.financialExpectations.preferredTo ?? null,
            eliminatingAbove: r.financialExpectations.eliminationThreshold ?? null,
            currency: r.financialExpectations.currency ?? "PLN",
            exceedingBudgetIsEliminating: r.financialExpectations.isEliminating ?? false,
            weight: r.financialExpectations.weight ?? 0,
            scoring: findLocalCriterionScoring(r.localCriteria, LOCAL_CRITERION_NAMES.financialExpectations)
                ?? createEmptyFinancialExpectations().scoring,
        } : createEmptyFinancialExpectations(),
        availability: r.availability ? {
            earliestAcceptedStartDate: r.availability.earliestAcceptableStartDate ?? "",
            maxNoticePeriodDays: r.availability.maxNoticePeriodDays ?? null,
            aiAsksAboutAvailability: r.availability.askCandidate ?? true,
            lackOfQuickAvailabilityIsEliminating: r.availability.isEliminating ?? false,
            weight: r.availability.weight ?? 0,
            requiresShiftWorkReadiness: r.availability.readyForShifts ?? false,
            requiresWeekendReadiness: r.availability.readyForWeekends ?? false,
            requiresBusinessTripReadiness: r.availability.readyForDelegations ?? false,
            requiresOvertimeReadiness: r.availability.readyForOvertime ?? false,
            scoring: findLocalCriterionScoring(r.localCriteria, LOCAL_CRITERION_NAMES.availability)
                ?? createEmptyAvailability().scoring,
        } : createEmptyAvailability(),
        disqualificationRules: (r.disqualificationRules ?? []).map(dr => ({
            name: dr.name ?? "",
            conditionDescription: dr.condition ?? "",
            relatedFormField: dr.relatedField ?? "",
            eliminatingValue: dr.eliminatingValue ?? "",
            isAutomaticElimination: dr.autoEliminate ?? false,
            requiresManualDecision: dr.requiresManualDecision ?? false,
            recruiterComment: dr.recruiterComment ?? "",
        })),
        aiConversationConfig: r.conversationConfig ? {
            conversationStyle: re("CONVERSATION_STYLE", r.conversationConfig.style) ?? "professional",
            language: r.conversationConfig.language ?? "pl",
            canAnswerAboutSalary: r.conversationConfig.canAnswerAboutSalary ?? true,
            canAnswerAboutBenefits: r.conversationConfig.canAnswerAboutBenefits ?? true,
            canAnswerAboutCompany: r.conversationConfig.canAnswerAboutCompany ?? true,
            confidentialInfo: r.conversationConfig.confidentialInfo ?? "",
            fallbackAnswer: r.conversationConfig.fallbackAnswer ?? "",
            maxFollowUpQuestions: r.conversationConfig.maxFollowUpQuestions ?? 2,
        } : createEmptyAiConversationConfig(),
        scoringConfig: r.scoringConfig ? {
            maxTotalPoints: r.scoringConfig.maxPoints ?? 100,
            veryGoodMatchThreshold: r.scoringConfig.excellentMatchThreshold ?? 85,
            goodMatchThreshold: r.scoringConfig.goodMatchThreshold ?? 65,
            partialMatchThreshold: r.scoringConfig.partialMatchThreshold ?? 40,
            eliminationBlocksRanking: r.scoringConfig.eliminationBlocksRanking ?? true,
            categoryMaxPoints: r.scoringConfig.categoryWeights ? {
                documents: r.scoringConfig.categoryWeights.documents ?? 0,
                criticalSkills: r.scoringConfig.categoryWeights.criticalSkills ?? 0,
                optionalSkills: r.scoringConfig.categoryWeights.optionalSkills ?? 0,
                experience: r.scoringConfig.categoryWeights.experience ?? 0,
                availability: r.scoringConfig.categoryWeights.availability ?? 0,
                financialExpectations: r.scoringConfig.categoryWeights.financialExpectations ?? 0,
                motivation: r.scoringConfig.categoryWeights.motivation ?? 0,
                safety: r.scoringConfig.categoryWeights.safetyAndWorkConditions ?? 0,
                overtimeAndShifts: r.scoringConfig.categoryWeights.overtimeAndShifts ?? 0,
                customQuestions: r.scoringConfig.categoryWeights.customQuestions ?? 0,
            } : createEmptyScoringConfig().categoryMaxPoints,
        } : createEmptyScoringConfig(),
        sectionAdditionalInfo: {
            ...createEmptySectionAdditionalInfo(),
            ...Object.fromEntries(
                Object.entries(r.sectionAdditionalInfo ?? {}).map(([k, v]) => [k, v ?? ""])
            ),
        },
        hiddenSections: [],
        // Preserve any overrides/local criteria this mapper doesn't specifically
        // hydrate into a section above, so round-tripping a save never drops them.
        scoringOverrides: r.scoringOverrides ?? [],
        localCriteria: (r.localCriteria ?? []).filter(
            (lc) => !Object.values(LOCAL_CRITERION_NAMES).includes(lc.name) && !ownedLocalCriterionNames.has(lc.name)
        ),
    };
}

export function toUpdateRequest(jobOffer, { isDraft = false } = {}) {
    const { scenarioId: _s, ...rest } = toApiRequest(null, jobOffer, { isDraft });
    return { ...rest, draftId: jobOffer.id ?? null, scenarioId: jobOffer.scenarioId ?? null };
}

export function toApiRequest(scenarioId, jobOffer, { isDraft = false } = {}) {
    const { basicInfo, positionDescription } = jobOffer;
    const keep = (arr, predicate) => (isDraft ? (arr ?? []) : (arr ?? []).filter(predicate));
    return {
        scenarioId,
        jobTitle: basicInfo?.positionName,
        department: basicInfo?.department,
        companyName: basicInfo?.companyName,
        companyDescription: basicInfo?.companyDescription || null,
        location: basicInfo?.location,
        workMode: e("WORK_MODE", basicInfo?.workMode),
        vacancyCount: basicInfo?.vacancies,
        plannedStartDate: basicInfo?.plannedStartDateIsAsap ? null : (basicInfo?.plannedStartDate || null),
        plannedStartDateIsAsap: basicInfo?.plannedStartDateIsAsap ?? false,
        seniorityLevel: e("SENIORITY_LEVEL", basicInfo?.seniorityLevel),
        status: e("OFFER_STATUS", basicInfo?.offerStatus),
        sendNotifications: basicInfo?.sendNotifications ?? false,
        notificationEmails: basicInfo?.notificationEmails?.length ? basicInfo.notificationEmails : null,
        shortDescription: positionDescription?.shortDescription,
        mainDuties: positionDescription?.mainResponsibilities ?? [],
        responsibilities: positionDescription?.responsibilitiesScope ?? [],
        goals: positionDescription?.positionGoals,
        typicalDay: positionDescription?.typicalWorkday,
        teamStructure: positionDescription?.teamStructure,
        supervisor: positionDescription?.supervisor,
        employeeOnboarding: positionDescription?.employeeOnboarding || null,
        tools: positionDescription?.toolsAndSystems ?? [],
        benefits: positionDescription?.benefits ?? [],
        recruitmentStages: positionDescription?.recruitmentStages ?? [],
        employmentForms: keep(jobOffer.employmentForms, ef => ef.type && ef.type.length > 0).map(mapEmploymentForm),
        salaryModels: keep(jobOffer.salaryModels, sm => sm.type).map(mapSalaryModel),
        bonuses: keep(jobOffer.bonuses, b => b.type).map(mapBonus),
        allowances: keep(jobOffer.allowances, a => a.type).map(mapAllowance),
        workingTime: mapWorkingTime(jobOffer.workingTime),
        workConditions: mapWorkConditions(jobOffer.workConditions),
        riskFactors: (jobOffer.riskFactors ?? []).map(mapRiskFactor),
        protectiveEquipment: (jobOffer.protectiveEquipment ?? []).map(mapProtectiveEquipment),
        requiredDocuments: keep(jobOffer.requiredDocuments, rd => rd.name).map(mapRequiredDocument),
        skills: keep(jobOffer.skills, sk => sk.name && sk.category).map(mapSkill),
        experienceRequirements: keep(jobOffer.experienceRequirements, er => er.area).map(mapExperienceRequirement),
        customQuestions: keep(jobOffer.customQuestions, cq => cq.questionText && cq.answerType).map(mapCustomQuestion),
        motivationQuestions: (jobOffer.motivationQuestions ?? []).map(mapMotivationQuestion),
        financialExpectations: mapFinancialExpectations(jobOffer.financialExpectations),
        availability: mapAvailability(jobOffer.availability),
        disqualificationRules: (jobOffer.disqualificationRules ?? []).map(mapDisqualificationRule),
        conversationConfig: mapConversationConfig(jobOffer.aiConversationConfig),
        scoringConfig: mapScoringConfig(jobOffer.scoringConfig),
        sectionAdditionalInfo: mapSectionAdditionalInfo(jobOffer.sectionAdditionalInfo),
        ownerId: null,
        // scoringOverrides: passed through as-is (already wire-shaped, see fromApiResponse).
        // localCriteria: scoring-capable sections that produced one (e.g. availability) + any
        // pass-through entries this mapper doesn't own.
        scoringOverrides: jobOffer.scoringOverrides ?? [],
        localCriteria: [
            ...(isScoringConfigured(jobOffer.availability?.scoring)
                ? [buildLocalCriterionRequest(LOCAL_CRITERION_NAMES.availability, jobOffer.availability.scoring)]
                : []),
            ...(isScoringConfigured(jobOffer.financialExpectations?.scoring)
                ? [buildLocalCriterionRequest(LOCAL_CRITERION_NAMES.financialExpectations, jobOffer.financialExpectations.scoring)]
                : []),
            ...buildArrayLocalCriterionRequests(jobOffer.employmentForms, (ef) => ef.aiQuestion),
            ...buildArrayLocalCriterionRequests(jobOffer.motivationQuestions, (mq) => mq.questionText),
            ...buildArrayLocalCriterionRequests(jobOffer.requiredDocuments, (rd) => rd.name),
            ...buildArrayLocalCriterionRequests(jobOffer.skills, (sk) => sk.name),
            ...buildArrayLocalCriterionRequests(jobOffer.experienceRequirements, (er) => er.area),
            ...buildArrayLocalCriterionRequests(jobOffer.riskFactors, (rf) => rf.name),
            ...buildArrayLocalCriterionRequests(jobOffer.customQuestions, (cq) => cq.questionText),
            ...(jobOffer.localCriteria ?? []),
        ],
    };
}
